import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import analytics from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * The Founder's List signup form — reusable, editorial, matches site aesthetic.
 * Variants: 'card' (default full editorial card), 'inline' (footer), 'panel' (dedicated page).
 */
export default function FoundersListForm({
  source = 'homepage',
  variant = 'card',
  headline,
  redirectOnSuccess = false,
}) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // null | 'created' | 'resubscribed' | 'already_active'
  const navigate = useNavigate();

  const validate = () => {
    const e = {};
    if (!firstName.trim()) e.first_name = 'Please enter your first name.';
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) e.email = 'Please enter a valid email address.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setErrors({});
    try {
      const resp = await axios.post(`${API}/newsletter`, {
        email: email.trim(),
        first_name: firstName.trim(),
        source,
        consent_given: true,
        website, // honeypot
      });
      const result = resp.headers['x-newsletter-result'] || 'created';
      setSuccess(result);
      // GA4 event — captures conversion + source
      analytics.track('founders_list_signup', {
        source,
        result,
      });
      if (redirectOnSuccess) {
        navigate('/founders-list/confirmed', { state: { firstName: firstName.trim(), result } });
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error('You are moving fast. Please wait a moment and try again.');
      } else if (err?.code === 'ERR_NETWORK') {
        setErrors({ form: 'Something interrupted the connection. Please check your internet connection and try again.' });
      } else {
        setErrors({ form: "We couldn't add you to The Founder's List right now. Please try again in a moment." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success && !redirectOnSuccess) {
    const heading =
      success === 'already_active'
        ? "You're already on The Founder's List."
        : success === 'resubscribed'
          ? 'Welcome back to The Founder’s List.'
          : 'You’re officially on The Founder’s List.';
    const body =
      success === 'already_active'
        ? 'Keep an eye on your inbox for the next Founder’s Brief.'
        : 'Your first email is on the way. Add hello@iamtoiawallace.com to your contacts so The Founder’s Brief always lands in your inbox.';
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="founders-list-success"
        className={variant === 'inline' ? 'text-ivory/85' : 'text-matte'}
      >
        <div
          className={
            variant === 'inline'
              ? 'font-serif-display text-2xl leading-tight'
              : 'font-serif-display text-3xl md:text-4xl leading-[1.15] tracking-tight'
          }
        >
          {heading}
        </div>
        <p className={`mt-4 leading-relaxed ${variant === 'inline' ? 'text-ivory/70 text-sm' : 'text-matte/70'}`}>{body}</p>
      </div>
    );
  }

  const isInline = variant === 'inline';
  const inputBase = isInline
    ? 'w-full bg-transparent border-0 border-b border-ivory/25 focus:border-gold py-3 text-sm text-ivory placeholder-ivory/40 focus:outline-none transition-colors'
    : 'w-full bg-transparent border-0 border-b border-taupe/40 focus:border-matte py-3 text-[15px] text-matte placeholder-matte/40 focus:outline-none transition-colors';

  return (
    <form onSubmit={handleSubmit} noValidate data-testid={`founders-list-form-${variant}`} className={isInline ? '' : 'space-y-5'}>
      {headline && !isInline && (
        <div className="font-serif-display text-3xl md:text-4xl leading-[1.15] tracking-tight text-matte mb-8">
          {headline}
        </div>
      )}

      {/* Honeypot — visually hidden but present in DOM */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
      </div>

      <div className={isInline ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : 'grid grid-cols-1 sm:grid-cols-2 gap-6'}>
        <div>
          <label htmlFor={`fl-first-${variant}`} className={`block eyebrow mb-2 ${isInline ? 'text-ivory/60' : 'text-taupe'}`}>
            First Name
          </label>
          <input
            id={`fl-first-${variant}`}
            data-testid="founders-list-input-first-name"
            type="text"
            autoComplete="given-name"
            required
            aria-required="true"
            aria-invalid={!!errors.first_name}
            aria-describedby={errors.first_name ? `fl-first-err-${variant}` : undefined}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className={inputBase}
          />
          {errors.first_name && (
            <div id={`fl-first-err-${variant}`} role="alert" className="mt-2 text-xs text-red-600">
              {errors.first_name}
            </div>
          )}
        </div>
        <div>
          <label htmlFor={`fl-email-${variant}`} className={`block eyebrow mb-2 ${isInline ? 'text-ivory/60' : 'text-taupe'}`}>
            Email
          </label>
          <input
            id={`fl-email-${variant}`}
            data-testid="founders-list-input-email"
            type="email"
            autoComplete="email"
            required
            aria-required="true"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? `fl-email-err-${variant}` : undefined}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your best email"
            className={inputBase}
          />
          {errors.email && (
            <div id={`fl-email-err-${variant}`} role="alert" className="mt-2 text-xs text-red-600">
              {errors.email}
            </div>
          )}
        </div>
      </div>

      {errors.form && (
        <div role="alert" className={`mt-4 text-sm ${isInline ? 'text-ivory/80' : 'text-red-700 bg-red-50/70 border border-red-100 px-3 py-2 rounded'}`}>
          {errors.form}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid="founders-list-submit"
        className={
          isInline
            ? 'mt-6 w-full sm:w-auto btn-primary bg-ivory text-matte hover:bg-gold hover:text-matte disabled:opacity-60'
            : 'mt-8 w-full sm:w-auto btn-primary disabled:opacity-60'
        }
      >
        {submitting ? 'Joining The List…' : 'Join The Founder’s List'}
      </button>

      <p className={`mt-6 text-[11px] leading-relaxed ${isInline ? 'text-ivory/50' : 'text-matte/55'}`}>
        By joining, you agree to receive emails from Toia Wallace. You may unsubscribe at any time.
      </p>
    </form>
  );
}
